// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { mergeStyles } from '@fluentui/react';
import { ArrowClockwise16Regular, CallEnd20Filled, Checkmark20Regular, CheckmarkCircle16Regular, Circle16Regular, Delete20Regular, Dismiss20Regular, Dismiss16Regular, Edit20Regular, ErrorCircle16Regular, Eye16Regular, MicOff16Filled, MicOff16Regular, MicOff20Filled, Mic16Filled, Mic20Filled, Mic20Regular, MoreHorizontal20Filled, MoreHorizontal20Regular, People20Filled, Settings20Filled, Send20Filled, Send20Regular, ShareScreenStart20Filled, ShareScreenStop20Filled, Speaker220Regular, Video16Filled, Video20Filled, Video20Regular, VideoOff20Filled, ChevronLeft20Regular, ChevronRight20Regular, WifiWarning20Filled, SpeakerMute16Filled, MicProhibited16Filled, VideoProhibited16Filled } from '@fluentui/react-icons';
/* @conditional-compile-remove(raise-hand) */
import { HandRight20Filled, HandRightOff20Filled, HandRight20Regular, HandRightOff20Regular } from '@fluentui/react-icons';
/* @conditional-compile-remove(close-captions) */
import { ClosedCaption20Regular, ClosedCaptionOff20Regular, Settings20Regular, PersonVoice20Regular, Translate20Regular } from '@fluentui/react-icons';
/* @conditional-compile-remove(video-background-effects) */
import { VideoBackgroundEffect20Filled, VideoBackgroundEffect20Regular } from '@fluentui/react-icons';
/* @conditional-compile-remove(pinned-participants) */
import { Pin16Filled, Pin16Regular, PinOff16Regular, ScaleFit20Regular, ScaleFill20Regular } from '@fluentui/react-icons';
import { _pxToRem } from '@internal/acs-ui-common';
import React from 'react';
import { useTheme } from './FluentThemeProvider';
/**
 * Icons used by the React components exported from this library.
 *
 * @remark See {@link CompositeIcons} for icons used by composites only.
 *
 * @public
 */
export type ComponentIcons = Record<keyof typeof DEFAULT_COMPONENT_ICONS, JSX.Element>;
const WifiWarning16Filled = (): JSX.Element =>
// All ErrorBar icons are 16px x 16px (when 1rem = 16 px).
// There is no 16px version of this icon in the fluent icon package, so scale the larger
// one down to required size.
<div className={mergeStyles({
  transform: 'scale(0.8)'
})}>
    <WifiWarning20Filled />
  </div>;
const MoreHorizontal18Regular = (): JSX.Element =>
// MoreHorizontal icons are 16px x 16px or 20px x 20px so scaling to get desired size
<div className={mergeStyles({
  transform: 'scale(0.9)'
})}>
    <MoreHorizontal20Regular />
  </div>;
const GalleryLeftButton = (): JSX.Element => {
  const rtl = useTheme().rtl;
  return rtl ? <ChevronRight20Regular /> : <ChevronLeft20Regular />;
};
const GalleryRightButton = (): JSX.Element => {
  const rtl = useTheme().rtl;
  return rtl ? <ChevronLeft20Regular /> : <ChevronRight20Regular />;
};

/**
 * The default set of icons that are available to use in the UI components.
 *
 * @remark Icons used only in the composites are available in {@link DEFAULT_COMPOSITE_ICONS}.
 *
 * @public
 */
export const DEFAULT_COMPONENT_ICONS = {
  ChatMessageOptions: <MoreHorizontal18Regular />,
  ControlButtonCameraOff: <VideoOff20Filled />,
  ControlButtonCameraOn: <Video20Filled />,
  ControlButtonEndCall: <CallEnd20Filled />,
  ControlButtonMicOff: <MicOff20Filled />,
  ControlButtonMicOn: <Mic20Filled />,
  ControlButtonOptions: <Settings20Filled />,
  ControlButtonParticipants: <People20Filled />,
  ControlButtonScreenShareStart: <ShareScreenStart20Filled />,
  ControlButtonScreenShareStop: <ShareScreenStop20Filled />,
  /* @conditional-compile-remove(raise-hand) */
  ControlButtonRaiseHand: <HandRight20Filled />,
  /* @conditional-compile-remove(raise-hand) */
  ControlButtonLowerHand: <HandRightOff20Filled />,
  /* @conditional-compile-remove(raise-hand) */
  RaiseHandContextualMenuItem: <HandRight20Regular />,
  /* @conditional-compile-remove(raise-hand) */
  LowerHandContextualMenuItem: <HandRightOff20Regular />,
  EditBoxCancel: <Dismiss20Regular />,
  EditBoxSubmit: <Checkmark20Regular />,
  ErrorBarCallCameraAccessDenied: <VideoProhibited16Filled />,
  ErrorBarCallCameraAlreadyInUse: <VideoProhibited16Filled />,
  ErrorBarCallLocalVideoFreeze: <WifiWarning16Filled />,
  ErrorBarCallMacOsCameraAccessDenied: <VideoProhibited16Filled />,
  ErrorBarCallMacOsMicrophoneAccessDenied: <MicProhibited16Filled />,
  ErrorBarCallMicrophoneAccessDenied: <MicProhibited16Filled />,
  ErrorBarCallMicrophoneMutedBySystem: <MicOff16Filled />,
  ErrorBarCallMicrophoneUnmutedBySystem: <Mic16Filled />,
  ErrorBarCallNetworkQualityLow: <WifiWarning16Filled />,
  ErrorBarCallNoMicrophoneFound: <MicProhibited16Filled />,
  ErrorBarCallNoSpeakerFound: <SpeakerMute16Filled />,
  ErrorBarClear: <Dismiss16Regular />,
  ErrorBarCallVideoRecoveredBySystem: <Video16Filled />,
  ErrorBarCallVideoStoppedBySystem: <VideoProhibited16Filled />,
  HorizontalGalleryLeftButton: <GalleryLeftButton />,
  HorizontalGalleryRightButton: <GalleryRightButton />,
  MessageDelivered: <CheckmarkCircle16Regular />,
  MessageEdit: <Edit20Regular />,
  MessageFailed: <ErrorCircle16Regular />,
  MessageRemove: <Delete20Regular />,
  MessageResend: <ArrowClockwise16Regular />,
  MessageSeen: <Eye16Regular />,
  MessageSending: <Circle16Regular />,
  OptionsCamera: <Video20Regular />,
  OptionsMic: <Mic20Regular />,
  OptionsSpeaker: <Speaker220Regular />,
  ParticipantItemMicOff: <MicOff16Regular />,
  ParticipantItemOptions: <></>,
  ParticipantItemOptionsHovered: <MoreHorizontal20Filled />,
  ParticipantItemScreenShareStart: <ShareScreenStart20Filled />,
  SendBoxSend: <Send20Regular />,
  SendBoxSendHovered: <Send20Filled />,
  VideoTileMicOff: <MicOff16Filled />,
  /* @conditional-compile-remove(pinned-participants) */
  VideoTilePinned: <Pin16Filled />,
  /* @conditional-compile-remove(pinned-participants) */
  VideoTileMoreOptions: <MoreHorizontal20Filled />,
  /* @conditional-compile-remove(pinned-participants) */
  VideoTileScaleFit: <ScaleFit20Regular />,
  /* @conditional-compile-remove(pinned-participants) */
  VideoTileScaleFill: <ScaleFill20Regular />,
  /* @conditional-compile-remove(pinned-participants) */
  PinParticipant: <Pin16Regular />,
  /* @conditional-compile-remove(pinned-participants) */
  UnpinParticipant: <PinOff16Regular />,
  SplitButtonPrimaryActionCameraOn: <Video20Filled />,
  SplitButtonPrimaryActionCameraOff: <VideoOff20Filled />,
  SplitButtonPrimaryActionMicUnmuted: <Mic20Filled />,
  SplitButtonPrimaryActionMicMuted: <MicOff20Filled />,
  /* @conditional-compile-remove(vertical-gallery) */
  VerticalGalleryLeftButton: <GalleryLeftButton />,
  /* @conditional-compile-remove(vertical-gallery) */
  VerticalGalleryRightButton: <GalleryRightButton />,
  /* @conditional-compile-remove(video-background-effects) */
  ControlButtonVideoEffectsOption: <VideoBackgroundEffect20Regular />,
  /* @conditional-compile-remove(video-background-effects) */
  ConfigurationScreenVideoEffectsButton: <VideoBackgroundEffect20Filled />,
  /* @conditional-compile-remove(close-captions) */
  CaptionsIcon: <ClosedCaption20Regular />,
  /* @conditional-compile-remove(close-captions) */
  CaptionsOffIcon: <ClosedCaptionOff20Regular />,
  /* @conditional-compile-remove(close-captions) */
  CaptionsSettingsIcon: <Settings20Regular />,
  /* @conditional-compile-remove(close-captions) */
  ChangeSpokenLanguageIcon: <PersonVoice20Regular />,
  /* @conditional-compile-remove(close-captions) */
  ChangeCaptionLanguageIcon: <Translate20Regular />,
  ContextMenuCameraIcon: <Video20Regular />,
  ContextMenuMicIcon: <Mic20Regular />,
  ContextMenuSpeakerIcon: <Speaker220Regular />
};"../../../acs-ui-common/src"